import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlignLeft,
  FileText,
  Gauge,
  Hash,
  Shapes,
  Tags as TagsIcon,
  Type,
} from 'lucide-react';
import { STEP_HEADING } from '../components/fileCardStyles';
import Panel from '../components/Panel';
import FieldLabel, { Required } from '../components/FieldLabel';
import DifficultyPicker from '../components/DifficultyPicker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SearchSelect from '@/components/ui/search-select';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import {
  selectV2,
  setBasics,
  addTag,
  removeTag,
  setStep,
} from '../v2Upload.slice';
import WizardFooter from '../components/WizardFooter';
import FieldError, { redlineControl } from '../components/FieldError';
import TagInput from '../components/TagInput';
import useDraftSync from '../useDraftSync';
import useFieldErrors from '../useFieldErrors';
import { missingForDraft } from '../utils/validation';

const HINT = 'text-xs text-muted-foreground mt-1.5';

export default function BasicsStep() {
  const dispatch = useDispatch();
  const { basics } = useSelector(selectV2);
  const [localTitle, setLocalTitle] = useState(basics.title);
  const [localDesc, setLocalDesc] = useState(basics.description);
  const [saving, setSaving] = useState(false);
  const { syncBasics } = useDraftSync();
  const { data: categories } = useGetCategoriesQuery({
    type: ICategoryType.POST,
  });

  const update = (partial: Record<string, string>) => {
    dispatch(setBasics(partial));
  };

  const missing = missingForDraft(basics);
  const { bad, props, guard } = useFieldErrors(missing);

  const handleContinue = async () => {
    if (!guard()) return;
    setSaving(true);
    const ok = await syncBasics();
    setSaving(false);
    if (ok) dispatch(setStep(2));
  };

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>Start your project listing</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-[640px]">
        Begin with a title and short description. You&apos;ll upload all project
        files, including photos, in the next step.
      </p>

      <div className="max-w-[1400px] space-y-5">
        <Panel
          icon={FileText}
          title="Project Basics"
          help="The name and blurb a maker reads first. Be specific — 'Walnut phone dock' beats 'my project' — and say what makes it worth cutting."
        >
          <div {...props('title')}>
            <FieldLabel icon={Type}>
              Project Title
              <Required />
            </FieldLabel>
            <Input
              value={localTitle}
              onChange={(e) => {
                setLocalTitle(e.target.value);
                update({ title: e.target.value });
              }}
              placeholder="e.g. Walnut phone dock"
              className={redlineControl(bad('title'))}
            />
            <FieldError missing={missing} field="title" show={bad('title')} />
          </div>

          <div {...props('description')}>
            <FieldLabel icon={AlignLeft}>
              Short Description
              <Required />
            </FieldLabel>
            <Textarea
              value={localDesc}
              onChange={(e) => {
                setLocalDesc(e.target.value);
                update({ description: e.target.value });
              }}
              placeholder="What it is, the wood, anything a maker should know…"
              rows={3}
              className={redlineControl(bad('description'))}
            />
            <FieldError
              missing={missing}
              field="description"
              show={bad('description')}
            />
            <p className={HINT}>
              Keep it short and clear—highlight what makes your project unique.
            </p>
          </div>
        </Panel>

        <Panel
          icon={TagsIcon}
          title="Project Info"
          help="How people find this project: the category decides where it turns up when they browse, the difficulty sets expectations, and tags are what search matches on."
          // the tag suggestions drop past the panel's bottom edge
          className="overflow-visible"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div {...props('category')}>
              <FieldLabel icon={Shapes}>
                Category
                <Required />
              </FieldLabel>
              <SearchSelect
                aria-label="Category"
                value={basics.category}
                options={(categories ?? []).map((c) => c.name)}
                onChange={(v) => update({ category: v })}
                placeholder="Select a category"
                invalid={bad('category')}
              />
              <FieldError
                missing={missing}
                field="category"
                show={bad('category')}
              />
            </div>

            <div {...props('difficulty')}>
              <FieldLabel icon={Gauge}>
                Project Difficulty
                <Required />
              </FieldLabel>
              <DifficultyPicker
                value={basics.difficulty}
                onChange={(v) => update({ difficulty: v })}
                invalid={bad('difficulty')}
              />
              <FieldError
                missing={missing}
                field="difficulty"
                show={bad('difficulty')}
              />
            </div>
          </div>

          <div {...props('tags')}>
            <FieldLabel icon={Hash}>Tags</FieldLabel>
            <TagInput
              tags={basics.tags}
              onAdd={(t) => dispatch(addTag(t))}
              onRemove={(i) => dispatch(removeTag(i))}
              invalid={bad('tags')}
            />
            <FieldError missing={missing} field="tags" show={bad('tags')} />
            <p className={HINT}>
              Add relevant tags to help makers discover your project.
            </p>
          </div>
        </Panel>
      </div>

      <WizardFooter
        onBack={() => dispatch(setStep(0))}
        onContinue={handleContinue}
        busy={saving}
        busyLabel="Saving draft…"
        backDisabled={saving}
        continueDisabled={false}
      />
    </div>
  );
}
